import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  Copy,
  ExternalLink,
  Mail,
  Pencil,
  Plus,
  Radio,
  Trash2,
} from "lucide-react";
import { z } from "zod";
import {
  ChannelStatusSchema,
  ChannelTypeSchema,
  EmailLocalPartSchema,
  EmailSubdomainSchema,
  UrlSchema,
  type Channel,
  type EmailAddressAvailability,
  type Rooftop,
  type UpdateChannelDto,
  type WidgetCustomization,
} from "@lumika/shared";
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  PageError,
  PageLoading,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { formatDateTime, humanize } from "../../lib/format";

type ChannelType = z.infer<typeof ChannelTypeSchema>;
type ChannelStatus = z.infer<typeof ChannelStatusSchema>;

// The only types an admin may create by hand — token channels whose
// whole identity is server-minted. Everything else (phone/sms via the
// Telephony tab, social/email via their connect flows) is born in its
// provisioning flow; the edit dialog renders those types read-only.
const MANUAL_CREATE_TYPES: ChannelType[] = ["web", "api"];

const CHANNEL_TYPE_LABELS: Record<ChannelType, string> = {
  web: "Website widget",
  web_form: "Web form",
  email_alias: "Email alias",
  email_adf_inbound: "Email — ADF inbound",
  gmail_oauth: "Gmail (OAuth)",
  outlook_oauth: "Outlook (OAuth)",
  phone: "Voice",
  sms: "SMS",
  facebook_messenger: "Facebook Messenger",
  instagram: "Instagram DM",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  api: "API",
  aap: "Auto Agent Protocol",
};

const STATUS_VARIANT: Record<ChannelStatus, "default" | "success" | "warning" | "secondary" | "destructive"> = {
  draft: "secondary",
  provisioning: "default",
  active: "success",
  paused: "warning",
  failing: "destructive",
  released: "secondary",
  blocked_compliance: "warning",
};

/**
 * Lumika-provisioned email channels split into two kinds:
 *  - `email_adf_inbound` is always platform-owned and immutable.
 *  - `email_alias` is locked when it's an auto-provisioned alias, but
 *    aliases onboarded through the "Connect email" flow carry
 *    `config.purpose === "custom_alias"` and stay editable/deletable.
 * The backend rejects edit/delete on locked channels, so the UI hides
 * those actions to match.
 */
function isLockedChannel(c: Channel): boolean {
  // email_adf_inbound / email_alias are produced exclusively by platform
  // provisioning, so the type alone identifies a system channel.
  if (c.type === "email_adf_inbound") return true;
  if (c.type === "email_alias") {
    return (c.config as { purpose?: string }).purpose !== "custom_alias";
  }
  return false;
}

/**
 * Connected-account avatar inferred at connect time, with a neutral
 * initial-chip fallback for channels the provider gives no avatar for.
 */
function ChannelAvatar({ channel }: { channel: Channel }) {
  if (channel.externalAccountAvatarUrl) {
    return (
      <img
        src={channel.externalAccountAvatarUrl}
        alt=""
        className="h-7 w-7 shrink-0 rounded-full border border-border object-cover"
      />
    );
  }
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-xs font-semibold text-muted-foreground"
      aria-hidden
    >
      {channel.name.slice(0, 1).toUpperCase()}
    </span>
  );
}

function isTokenChannelType(type: ChannelType): boolean {
  return type === "web" || type === "api";
}

// Sentinel Select value for a group-wide channel (rooftopId = null).
// Only web/api channels may be group-wide; every other type stays
// pinned to a single rooftop.
const ALL_ROOFTOPS = "__all__";

/**
 * `<input type="color">` always returns a `#rrggbb` string, but the
 * field is optional in our schema so empty must be valid too. Pinning
 * the regex pattern matches the backend `WidgetCustomizationSchema`
 * so a paste-in admin can't sneak through an `rgb()` or 3-digit hex
 * value the widget CSS doesn't accept.
 */
const HexColorOrEmpty = z
  .string()
  .optional()
  .refine((v) => !v || /^#[0-9a-fA-F]{6}$/u.test(v), {
    message: "Use #rrggbb hex (e.g. #133b54).",
  });

const ChannelFormSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required.").max(80),
    type: ChannelTypeSchema,
    status: ChannelStatusSchema,
    // A real rooftop uuid, or the ALL_ROOFTOPS sentinel for group-wide
    // web/api channels. The superRefine below enforces a real rooftop
    // for every non-token type.
    rooftopId: z.string().min(1, "Select a rooftop."),
    // No provider / external identity / e164 here: those are owned by
    // the connect/provisioning flow that created the channel and are
    // displayed read-only in the dialog. Hand-entering them fabricated
    // rows the runtime couldn't use (and `lumika` email rows the
    // system-channel guard locks forever).
    /**
     * Free-form config for non-token channel types. Hidden in the UI
     * for web/api channels — those use the structured allowedDomains
     * input instead.
     */
    configJson: z
      .string()
      .optional()
      .refine(
        (value) => {
          if (!value || !value.trim()) return true;
          try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed);
          } catch {
            return false;
          }
        },
        { message: "Config must be a JSON object." },
      ),
    /**
     * Comma-separated hostnames the widget WebSocket / public API
     * accepts. Empty = allow all origins. Surfaced only for web/api;
     * other types ignore it.
     */
    allowedDomains: z
      .string()
      .optional()
      .refine(
        (value) => {
          if (!value || !value.trim()) return true;
          return value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .every((host) => /^[a-zA-Z0-9.-]+$/.test(host));
        },
        { message: "Use hostnames only (no protocol or path), comma-separated." },
      ),
    // Per-account widget customization (web channels only). Stored
    // server-side in `channel.config.widgetCustomization`; the widget
    // fetches it on mount and renders brand colors / dealer logo /
    // custom copy. All fields optional — an empty form leaves the
    // widget at its hardcoded defaults.
    widgetTitle: z.string().trim().max(60).optional(),
    widgetSubtitle: z.string().trim().max(120).optional(),
    widgetWelcomeHeading: z.string().trim().max(120).optional(),
    widgetWelcomeDescription: z.string().trim().max(280).optional(),
    widgetQuickQuestion1: z.string().trim().max(120).optional(),
    widgetQuickQuestion2: z.string().trim().max(120).optional(),
    widgetQuickQuestion3: z.string().trim().max(120).optional(),
    widgetPrimaryBg: HexColorOrEmpty,
    widgetPrimaryFg: HexColorOrEmpty,
    widgetSecondaryBg: HexColorOrEmpty,
    widgetSecondaryFg: HexColorOrEmpty,
    widgetLogoUrl: z.union([z.literal(""), UrlSchema]).optional(),
    // Per-channel AI response delay in seconds. Empty = inherit the
    // agent-level default.
    aiResponseDelaySeconds: z
      .string()
      .optional()
      .refine((v) => !v || !v.trim() || /^\d+$/.test(v.trim()), {
        message: "Enter a whole number of seconds (0 or more).",
      }),
    // When true, new leads on this channel — and existing
    // open leads with no assignee — are routed to the
    // dealership's AI user instead of through the human round-robin.
    // Defaulted on for `web` and `api` at creation (see EMPTY and
    // the type-Select handler below); the dealership can flip it for
    // any channel.
    alwaysAssignToAi: z.boolean(),
  })
  .superRefine((data, ctx) => {
    // "All rooftops" is only valid for web/api channels; every other
    // type must be pinned to a concrete rooftop.
    if (data.rooftopId === ALL_ROOFTOPS && !isTokenChannelType(data.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rooftopId"],
        message: "Select a rooftop.",
      });
    }
  });

type ChannelFormValues = z.infer<typeof ChannelFormSchema>;

const EMPTY: ChannelFormValues = {
  name: "",
  type: "web",
  status: "active",
  rooftopId: "",
  configJson: "",
  allowedDomains: "",
  widgetTitle: "",
  widgetSubtitle: "",
  widgetWelcomeHeading: "",
  widgetWelcomeDescription: "",
  widgetQuickQuestion1: "",
  widgetQuickQuestion2: "",
  widgetQuickQuestion3: "",
  widgetPrimaryBg: "",
  widgetPrimaryFg: "",
  widgetSecondaryBg: "",
  widgetSecondaryFg: "",
  widgetLogoUrl: "",
  aiResponseDelaySeconds: "",
  // Default on for the EMPTY.type=`web` starting state; the type-
  // Select handler keeps this synced to the type-based default
  // whenever the operator changes type before saving a new channel.
  alwaysAssignToAi: true,
};

function toEditValues(channel: Channel): ChannelFormValues {
  // For web/api channels the structured `allowedDomains` field lives
  // in `config.allowedDomains`; `tokenHash` is internal and not
  // editable (the backend preserves it across updates). The
  // free-form `configJson` textarea is hidden for these types.
  const isTokenChannel = isTokenChannelType(channel.type);
  const config = channel.config ?? {};
  const allowedDomains = Array.isArray((config as Record<string, unknown>).allowedDomains)
    ? ((config as { allowedDomains: unknown[] }).allowedDomains as string[]).join(", ")
    : "";
  // Internal config keys are excluded from the free-form JSON
  // textarea so token channels don't expose `tokenHash` /
  // `widgetCustomization` as raw JSON. Those are surfaced through
  // structured form fields below.
  const internalKeys = new Set(["tokenHash", "allowedDomains", "widgetCustomization"]);
  // A connected email alias's routing identity (`email`) and its
  // editability marker (`purpose: "custom_alias"`) live in config too.
  // Keep them out of the textarea — a Config JSON edit that dropped
  // `purpose` would permanently lock the channel (see isLockedChannel).
  // The update mutation re-merges them from the existing row.
  if (channel.type === "email_alias") {
    internalKeys.add("purpose");
    internalKeys.add("email");
  }
  const configKeys = Object.keys(config).filter(
    (k) => config[k] !== undefined && !internalKeys.has(k),
  );
  const widget = readWidgetCustomization(config);
  return {
    name: channel.name,
    type: channel.type,
    status: channel.status,
    rooftopId: channel.rooftopId ?? ALL_ROOFTOPS,
    configJson:
      isTokenChannel || configKeys.length === 0
        ? ""
        : JSON.stringify(
            Object.fromEntries(configKeys.map((k) => [k, config[k]])),
            null,
            2,
          ),
    allowedDomains,
    widgetTitle: widget.title ?? "",
    widgetSubtitle: widget.subtitle ?? "",
    widgetWelcomeHeading: widget.chatWelcomeHeading ?? "",
    widgetWelcomeDescription: widget.chatWelcomeDescription ?? "",
    widgetQuickQuestion1: widget.chatWelcomeQuickQuestions?.[0] ?? "",
    widgetQuickQuestion2: widget.chatWelcomeQuickQuestions?.[1] ?? "",
    widgetQuickQuestion3: widget.chatWelcomeQuickQuestions?.[2] ?? "",
    widgetPrimaryBg: widget.brand?.colors?.primary?.[0]?.background ?? "",
    widgetPrimaryFg: widget.brand?.colors?.primary?.[0]?.foreground ?? "",
    widgetSecondaryBg: widget.brand?.colors?.secondary?.[0]?.background ?? "",
    widgetSecondaryFg: widget.brand?.colors?.secondary?.[0]?.foreground ?? "",
    widgetLogoUrl: widget.brand?.logo?.image?.url ?? "",
    aiResponseDelaySeconds:
      channel.aiResponseDelaySeconds != null
        ? String(channel.aiResponseDelaySeconds)
        : "",
    alwaysAssignToAi: channel.alwaysAssignToAi,
  };
}

function readWidgetCustomization(
  config: Record<string, unknown>,
): WidgetCustomization {
  const value = (config as { widgetCustomization?: unknown }).widgetCustomization;
  if (!value || typeof value !== "object") return {};
  return value as WidgetCustomization;
}

function toPayload(values: ChannelFormValues): UpdateChannelDto {
  const isTokenChannel = isTokenChannelType(values.type);
  const isWeb = values.type === "web";
  const config: Record<string, unknown> = isTokenChannel
    ? { allowedDomains: parseAllowedDomains(values.allowedDomains) }
    : values.configJson?.trim()
      ? (JSON.parse(values.configJson) as Record<string, unknown>)
      : {};
  if (isWeb) {
    const customization = buildWidgetCustomization(values);
    if (customization) {
      config.widgetCustomization = customization;
    }
  }
  // Provider identity (provider, external account, e164) is owned by
  // the channel's connect/provisioning flow — the DTO no longer
  // accepts it from forms at all.
  return {
    name: values.name.trim(),
    type: values.type,
    status: values.status,
    rooftopId: values.rooftopId === ALL_ROOFTOPS ? null : values.rooftopId,
    config,
    aiResponseDelaySeconds: values.aiResponseDelaySeconds?.trim()
      ? Number(values.aiResponseDelaySeconds.trim())
      : null,
    alwaysAssignToAi: values.alwaysAssignToAi,
  };
}

/**
 * Assemble the structured widget customization object from the flat
 * form fields. Returns `undefined` when nothing is set so the saved
 * config object stays empty for dealerships that don't customize —
 * the widget then renders with hardcoded legacy defaults.
 *
 * A color pair is only emitted when BOTH background and foreground
 * are set (defensive: a half-set pair would leave the widget's
 * `var(--primary-text-color)` consumers undefined).
 */
function buildWidgetCustomization(
  values: ChannelFormValues,
): WidgetCustomization | undefined {
  const title = values.widgetTitle?.trim() ?? "";
  const subtitle = values.widgetSubtitle?.trim() ?? "";
  const welcomeHeading = values.widgetWelcomeHeading?.trim() ?? "";
  const welcomeDescription = values.widgetWelcomeDescription?.trim() ?? "";
  const quickQuestions = [
    values.widgetQuickQuestion1?.trim() ?? "",
    values.widgetQuickQuestion2?.trim() ?? "",
    values.widgetQuickQuestion3?.trim() ?? "",
  ].filter(Boolean);
  const primaryBg = values.widgetPrimaryBg?.trim() ?? "";
  const primaryFg = values.widgetPrimaryFg?.trim() ?? "";
  const secondaryBg = values.widgetSecondaryBg?.trim() ?? "";
  const secondaryFg = values.widgetSecondaryFg?.trim() ?? "";
  const logoUrl = values.widgetLogoUrl?.trim() ?? "";

  const primary = primaryBg && primaryFg ? [{ background: primaryBg, foreground: primaryFg }] : undefined;
  const secondary =
    secondaryBg && secondaryFg ? [{ background: secondaryBg, foreground: secondaryFg }] : undefined;
  const brand =
    primary || secondary || logoUrl
      ? {
          ...(primary || secondary ? { colors: { ...(primary ? { primary } : {}), ...(secondary ? { secondary } : {}) } } : {}),
          ...(logoUrl ? { logo: { image: { url: logoUrl } } } : {}),
        }
      : undefined;

  const customization: WidgetCustomization = {
    ...(title ? { title } : {}),
    ...(subtitle ? { subtitle } : {}),
    ...(welcomeHeading ? { chatWelcomeHeading: welcomeHeading } : {}),
    ...(welcomeDescription ? { chatWelcomeDescription: welcomeDescription } : {}),
    ...(quickQuestions.length > 0 ? { chatWelcomeQuickQuestions: quickQuestions } : {}),
    ...(brand ? { brand } : {}),
  };
  return Object.keys(customization).length > 0 ? customization : undefined;
}

function parseAllowedDomains(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function DealershipChannelsPage() {
  const { dealershipId } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Channel | "new" | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Channel | null>(null);
  const [connectingEmail, setConnectingEmail] = useState(false);

  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });
  const channelsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "channels"],
    queryFn: () => adminApi.listChannels(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  const rooftops = detailQuery.data?.rooftops ?? [];
  const channels = channelsQuery.data?.items ?? [];
  const rooftopName = useMemo(() => new Map(rooftops.map((r) => [r.id, r.name])), [rooftops]);

  const deleteMutation = useMutation({
    mutationFn: async (channel: Channel) => {
      // Phone channels are mirrors of `twilio_phone_numbers` rows
      // owned by the Telephony tab — they can't be deleted here.
      // The Telephony tab's "Release" action drops the carrier
      // number AND auto-removes the mirror channel.
      if (channel.type === "phone") {
        throw new Error(
          "Phone channels are managed by the Telephony tab — release the number there.",
        );
      }
      await adminApi.removeChannel(dealershipId!, channel.id);
    },
    onSuccess: async () => {
      setPendingDelete(null);
      await invalidateChannels(qc, dealershipId);
      toast.success("Channel deleted");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Routing"
        title="Channels"
        description="Inbound and outbound lead surfaces. Each channel belongs to one rooftop."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setConnectingEmail(true)}>
              <Mail className="h-4 w-4" />
              Connect email
            </Button>
            <Button onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" />
              New channel
            </Button>
          </div>
        }
      />

      <Section title={`${channelsQuery.data?.total ?? 0} channels`}>
        {channelsQuery.isLoading ? (
          <PageLoading message="Loading channels…" />
        ) : channelsQuery.isError ? (
          <PageError
            title="Couldn't load channels"
            description="Try refreshing the page."
          />
        ) : channels.length === 0 ? (
          <EmptyState
            icon={<Radio className="h-5 w-5" />}
            title="No channels yet"
            description="Add the first channel to start routing customer leads into lumika."
            action={
              <Button onClick={() => setEditing("new")}>
                <Plus className="h-4 w-4" />
                Add channel
              </Button>
            }
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Rooftop</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>External account</TableHead>
                    <TableHead>Last updated</TableHead>
                    <TableHead className="w-32 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {channels.map((channel) => (
                    <TableRow key={channel.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <ChannelAvatar channel={channel} />
                          <span>{channel.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{CHANNEL_TYPE_LABELS[channel.type]}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {channel.rooftopId
                          ? (rooftopName.get(channel.rooftopId) ?? "—")
                          : "All rooftops"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[channel.status]}>
                          {humanize(channel.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {channel.type === "phone" && channel.e164 ? (
                          <span className="font-mono">{channel.e164}</span>
                        ) : (
                          (channel.externalAccountLabel ?? channel.externalAccountId ?? "—")
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(channel.updatedAt)}
                      </TableCell>
                      <TableCell>
                        {/* Actions in a single horizontal row — never a
                            vertical stack. Locked rooftop email channels
                            are platform-owned: open only, no edit/delete. */}
                        <div className="flex items-center justify-end gap-0.5">
                          {channel.deepLink && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Open connected account"
                              title="Open the connected account"
                              onClick={() =>
                                window.open(channel.deepLink!, "_blank", "noopener,noreferrer")
                              }
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                          {!isLockedChannel(channel) && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Edit channel"
                                title="Edit channel"
                                onClick={() => setEditing(channel)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Delete channel"
                                title="Delete channel"
                                onClick={() => setPendingDelete(channel)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {channels.map((channel) => (
                <MobileListRow
                  key={channel.id}
                  primary={
                    <span className="flex items-center gap-2">
                      <ChannelAvatar channel={channel} />
                      <span>{channel.name}</span>
                    </span>
                  }
                  secondary={
                    channel.rooftopId
                      ? (rooftopName.get(channel.rooftopId) ?? "—")
                      : "All rooftops"
                  }
                  tertiary={
                    channel.type === "phone" && channel.e164
                      ? channel.e164
                      : (channel.externalAccountLabel ?? channel.externalAccountId ?? undefined)
                  }
                  meta={
                    <>
                      <Badge variant="secondary">{CHANNEL_TYPE_LABELS[channel.type]}</Badge>
                      <Badge variant={STATUS_VARIANT[channel.status]}>
                        {humanize(channel.status)}
                      </Badge>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {formatDateTime(channel.updatedAt)}
                      </span>
                    </>
                  }
                  trailing={
                    <div className="flex items-center gap-0.5">
                      {channel.deepLink && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Open connected account"
                          title="Open the connected account"
                          onClick={() =>
                            window.open(channel.deepLink!, "_blank", "noopener,noreferrer")
                          }
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      {!isLockedChannel(channel) && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Edit channel"
                            title="Edit channel"
                            onClick={() => setEditing(channel)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Delete channel"
                            title="Delete channel"
                            onClick={() => setPendingDelete(channel)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      {editing !== null && dealershipId && (
        <ChannelFormDialog
          mode={editing === "new" ? "create" : "edit"}
          channel={editing === "new" ? null : editing}
          rooftops={rooftops}
          dealershipId={dealershipId}
          onClose={() => setEditing(null)}
          // Keep web/api dialog open after create so the admin can copy
          // the freshly minted token + snippet. Parent swaps to edit
          // mode with the new channel id so the onboarding section
          // renders without the admin having to reopen the row.
          onCreated={(created) => {
            if (isTokenChannelType(created.type)) {
              setEditing(created);
            } else {
              setEditing(null);
            }
          }}
        />
      )}

      <DeleteChannelDialog
        channel={pendingDelete}
        isPending={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />

      {connectingEmail && dealershipId && (
        <AdminEmailConnect
          dealershipId={dealershipId}
          rooftops={rooftops}
          onClose={() => setConnectingEmail(false)}
        />
      )}
    </div>
  );
}

/**
 * Onboards a new lumika-hosted email alias (`<localPart>@<subdomain>.<zone>`).
 * The zone is resolved server-side, so the availability check echoes the
 * full address back rather than the UI hardcoding it. Connected aliases
 * carry `config.purpose === "custom_alias"`, which keeps them editable —
 * unlike the auto-provisioned aliases `isLockedChannel` locks.
 */
function AdminEmailConnect({
  dealershipId,
  rooftops,
  onClose,
}: {
  dealershipId: string;
  rooftops: Rooftop[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [localPart, setLocalPart] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [rooftopId, setRooftopId] = useState<string>(ALL_ROOFTOPS);

  // Pre-validate locally so we only round-trip well-formed parts, and
  // debounce so each keystroke doesn't fire a request.
  const localOk = EmailLocalPartSchema.safeParse(localPart).success;
  const subdomainOk = EmailSubdomainSchema.safeParse(subdomain).success;
  const [debounced, setDebounced] = useState({ localPart: "", subdomain: "" });
  useEffect(() => {
    const handle = setTimeout(() => setDebounced({ localPart, subdomain }), 350);
    return () => clearTimeout(handle);
  }, [localPart, subdomain]);
  const debouncedValid =
    EmailLocalPartSchema.safeParse(debounced.localPart).success &&
    EmailSubdomainSchema.safeParse(debounced.subdomain).success;

  const availabilityQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "email-available", debounced.localPart, debounced.subdomain],
    queryFn: () =>
      adminApi.checkEmailAvailability(dealershipId, debounced.localPart, debounced.subdomain),
    enabled: debouncedValid,
    staleTime: 30_000,
    retry: false,
  });

  const availability: EmailAddressAvailability | undefined = availabilityQuery.data;
  const fresh =
    availability != null &&
    availability.localPart === debounced.localPart &&
    availability.subdomain === debounced.subdomain;
  const available = Boolean(fresh && availability?.available);

  let statusMessage: string | null = null;
  if (debouncedValid && availabilityQuery.isFetching) {
    statusMessage = "Checking availability…";
  } else if (availabilityQuery.isError) {
    statusMessage = "Couldn't check availability — edit the address to retry.";
  } else if (fresh && availability) {
    if (availability.available) {
      statusMessage = `${availability.address} is available.`;
    } else if (availability.subdomainTaken) {
      statusMessage = `The subdomain "${availability.subdomain}" is owned by another dealership.`;
    } else if (availability.localPartTaken) {
      statusMessage = `${availability.address} is already in use on this dealership.`;
    } else {
      statusMessage = `${availability.address} is unavailable.`;
    }
  }

  const connectMutation = useMutation({
    mutationFn: () =>
      adminApi.connectEmailChannel(dealershipId, {
        rooftopId: rooftopId === ALL_ROOFTOPS ? null : rooftopId,
        name: availability?.address ?? `${localPart}@${subdomain}`,
        localPart,
        subdomain,
      }),
    onSuccess: async () => {
      await invalidateChannels(qc, dealershipId);
      toast.success("Email channel connected");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect email</DialogTitle>
          <DialogDescription>
            Provision a lumika-hosted inbox. Customers email this address; replies
            route back through lumika.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Local part"
              required
              error={localPart && !localOk ? "Use 1-64 lowercase letters, numbers, or . _ + -" : undefined}
              description="The part before the @."
            >
              <Input
                value={localPart}
                onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
                placeholder="hello"
                autoFocus
              />
            </FormField>
            <FormField
              label="Subdomain"
              required
              error={subdomain && !subdomainOk ? "Use 1-63 lowercase letters, numbers, or hyphens." : undefined}
              description="A DNS label owned by this dealership."
            >
              <Input
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                placeholder="mydealership"
              />
            </FormField>
          </div>

          {statusMessage && (
            <p
              className={cn(
                "text-xs",
                available
                  ? "text-success"
                  : fresh || availabilityQuery.isError
                    ? "text-destructive"
                    : "text-muted-foreground",
              )}
            >
              {statusMessage}
            </p>
          )}

          <FormField
            label="Rooftop"
            optional
            description="Pin the inbox to one rooftop, or leave group-wide."
          >
            <Select value={rooftopId} onValueChange={setRooftopId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ROOFTOPS}>All rooftops (group-wide)</SelectItem>
                {rooftops.map((rooftop) => (
                  <SelectItem key={rooftop.id} value={rooftop.id}>
                    {rooftop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => connectMutation.mutate()}
            disabled={!available || connectMutation.isPending}
          >
            {connectMutation.isPending ? "Connecting…" : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelFormDialog({
  mode,
  channel,
  rooftops,
  dealershipId,
  onClose,
  onCreated,
}: {
  mode: "create" | "edit";
  channel: Channel | null;
  rooftops: Rooftop[];
  dealershipId: string;
  onClose: () => void;
  onCreated: (created: Channel) => void;
}) {
  const qc = useQueryClient();
  const initialValues = channel
    ? toEditValues(channel)
    : { ...EMPTY, rooftopId: rooftops[0]?.id ?? "" };
  const form = useZodForm(ChannelFormSchema, initialValues);

  const createMutation = useMutation({
    mutationFn: async (input: ChannelFormValues) => {
      // Manual creation is web/api only — every other type is born in
      // its connect/provisioning flow (Telephony tab for sms/voice,
      // OAuth flows for social/email). The picker only offers the two,
      // and the backend rejects anything else as defense in depth.
      if (input.type !== "web" && input.type !== "api") {
        throw new Error(
          "Only Website widget and API channels can be created here — others come from their connect flows.",
        );
      }
      const payload = toPayload(input);
      return adminApi.createChannel(dealershipId, {
        rooftopId: payload.rooftopId ?? null,
        type: input.type,
        name: payload.name ?? input.name.trim(),
        config: payload.config ?? {},
        aiResponseDelaySeconds: payload.aiResponseDelaySeconds,
        alwaysAssignToAi: payload.alwaysAssignToAi,
        status: payload.status ?? input.status,
      });
    },
    onSuccess: async (created) => {
      await invalidateChannels(qc, dealershipId);
      toast.success("Channel created");
      onCreated(created);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: ChannelFormValues) => {
      const payload = toPayload(input);
      // `purpose` / `email` are excluded from the Config JSON textarea
      // (toEditValues) — re-merge them from the existing row so the
      // backend's wholesale config replace can't drop them and lock
      // the alias. Mirrors the backend's own tokenHash re-injection.
      if (channel!.type === "email_alias") {
        const existing = channel!.config as { purpose?: unknown; email?: unknown };
        payload.config = {
          ...(payload.config as Record<string, unknown>),
          ...(existing.purpose !== undefined ? { purpose: existing.purpose } : {}),
          ...(existing.email !== undefined ? { email: existing.email } : {}),
        };
      }
      return adminApi.updateChannel(dealershipId, channel!.id, payload);
    },
    onSuccess: async () => {
      await invalidateChannels(qc, dealershipId);
      toast.success("Channel updated");
      // Token channels keep the dialog open — the integration section
      // is the point of editing them. Everything else closes on save,
      // matching the rest of the admin forms.
      if (!isTokenChannelType(channel!.type)) onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    if (mode === "create") createMutation.mutate(result.data);
    else updateMutation.mutate(result.data);
  }

  const { values, errors, setField } = form;
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isDirty = JSON.stringify(values) !== JSON.stringify(initialValues);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New channel" : "Edit channel"}</DialogTitle>
          <DialogDescription>
            Identity and routing for a customer lead surface.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 overflow-y-auto pr-1">
          <fieldset className="grid gap-3">
            <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Identity
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                className="sm:col-span-2"
                label="Display name"
                required
                error={errors.name}
              >
                <Input
                  value={values.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="Website chat"
                  autoFocus
                />
              </FormField>
              <FormField
                label="Type"
                required
                error={errors.type}
                description={
                  mode === "edit"
                    ? "Set at creation — a channel never changes type."
                    : "Other channel types are created by their connect flows (Telephony, OAuth, email provisioning)."
                }
              >
                <Select
                  value={values.type}
                  onValueChange={(v) => {
                    const next = v as ChannelType;
                    setField("type", next);
                    // Re-sync the "Always Assign to AI Employee"
                    // default to the type-based default (true for
                    // web/api).
                    setField("alwaysAssignToAi", isTokenChannelType(next));
                  }}
                  disabled={mode === "edit"}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {mode === "create" ? (
                      // Manual creation is token channels only — every
                      // other type is born in its connect flow.
                      MANUAL_CREATE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {CHANNEL_TYPE_LABELS[type]}
                        </SelectItem>
                      ))
                    ) : (
                      // Edit shows the channel's real type (read-only)
                      // even when it isn't manually creatable.
                      <SelectItem value={values.type}>
                        {CHANNEL_TYPE_LABELS[values.type]}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Status" required error={errors.status}>
                <Select
                  value={values.status}
                  onValueChange={(v) => setField("status", v as ChannelStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ChannelStatusSchema.options.map((status) => (
                      <SelectItem key={status} value={status}>
                        {humanize(status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField
                label="Rooftop"
                required
                error={errors.rooftopId}
                description={
                  isTokenChannelType(values.type)
                    ? "Web and API channels can serve every rooftop in the group."
                    : undefined
                }
              >
                <Select
                  value={values.rooftopId}
                  onValueChange={(v) => setField("rooftopId", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a rooftop" />
                  </SelectTrigger>
                  <SelectContent>
                    {isTokenChannelType(values.type) && (
                      <SelectItem value={ALL_ROOFTOPS}>All rooftops (group-wide)</SelectItem>
                    )}
                    {rooftops.map((rooftop) => (
                      <SelectItem key={rooftop.id} value={rooftop.id}>
                        {rooftop.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          </fieldset>

          {mode === "edit" && channel && (channel.externalAccountId || channel.e164) ? (
            <fieldset className="grid gap-3">
              <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Provider identity (read-only)
              </legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {channel.externalAccountId ? (
                  <FormField
                    label="External account ID"
                    description="Provider-issued. Deauthorize and reconnect to change."
                  >
                    <Input value={channel.externalAccountId} disabled />
                  </FormField>
                ) : null}
                {channel.externalAccountLabel ? (
                  <FormField label="External account label" description="Provider-issued.">
                    <Input value={channel.externalAccountLabel} disabled />
                  </FormField>
                ) : null}
                {channel.e164 ? (
                  <FormField
                    label="Phone number"
                    description="Provisioned from the Telephony tab."
                  >
                    <Input value={channel.e164} disabled />
                  </FormField>
                ) : null}
                <p className="sm:col-span-2 text-xs text-muted-foreground">
                  Provider identity is managed by the connection that created
                  this channel; auth credentials are stored encrypted and never
                  returned by the API.
                </p>
              </div>
            </fieldset>
          ) : null}

          <fieldset className="grid gap-3">
            <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              AI behavior
            </legend>
            <FormField
              label="Always Assign to AI Employee"
              description="New leads on this channel — and existing open leads with no assignee — are routed to the AI Employee. Operators can still reassign a lead manually at any time. Defaults on for Website widget and API channels."
            >
              <label className="flex cursor-pointer items-center gap-3">
                <Switch
                  checked={values.alwaysAssignToAi}
                  onCheckedChange={(checked) => setField("alwaysAssignToAi", checked)}
                />
                <span className="text-sm text-muted-foreground">
                  {values.alwaysAssignToAi ? "On" : "Off"}
                </span>
              </label>
            </FormField>
            <FormField
              label="AI response delay (seconds)"
              optional
              error={errors.aiResponseDelaySeconds}
              description="How long the AI user waits before auto-replying on this channel — the window for a human to answer first. Leave blank to use the agent's default."
            >
              <Input
                type="number"
                min={0}
                step={1}
                value={values.aiResponseDelaySeconds ?? ""}
                onChange={(e) => setField("aiResponseDelaySeconds", e.target.value)}
                placeholder="Agent default"
              />
            </FormField>
          </fieldset>

          {isTokenChannelType(values.type) ? (
            <fieldset className="grid gap-3">
              <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Allowed origins
              </legend>
              <FormField
                label="Allowed domains"
                optional
                error={errors.allowedDomains}
                description="Comma-separated hostnames the widget/API will accept (e.g. `dealer.example.com, www.dealer.example.com`). Leave empty to allow all origins."
              >
                <Input
                  value={values.allowedDomains ?? ""}
                  onChange={(e) => setField("allowedDomains", e.target.value)}
                  placeholder="dealer.example.com, www.dealer.example.com"
                />
              </FormField>
            </fieldset>
          ) : (
            <fieldset className="grid gap-3">
              <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Config (advanced)
              </legend>
              <FormField
                label="Config JSON"
                optional
                error={errors.configJson}
                description="Free-form configuration object stored on the channel. Used by integrations."
              >
                <Textarea
                  value={values.configJson ?? ""}
                  onChange={(e) => setField("configJson", e.target.value)}
                  rows={5}
                  className="font-mono text-xs"
                  placeholder='{ "greeting": "Hi, how can we help?" }'
                />
              </FormField>
            </fieldset>
          )}

          {values.type === "web" && (
            <fieldset className="grid gap-3">
              <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Widget customization
              </legend>
              <p className="text-xs text-muted-foreground">
                Per-account branding for the embedded widget. The widget
                fetches these values on mount; empty fields keep the
                hardcoded defaults.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Title" optional error={errors.widgetTitle}>
                  <Input
                    value={values.widgetTitle ?? ""}
                    onChange={(e) => setField("widgetTitle", e.target.value)}
                    placeholder="AI Assistant"
                  />
                </FormField>
                <FormField label="Subtitle" optional error={errors.widgetSubtitle}>
                  <Input
                    value={values.widgetSubtitle ?? ""}
                    onChange={(e) => setField("widgetSubtitle", e.target.value)}
                    placeholder="How can I help you today?"
                  />
                </FormField>
                <FormField
                  className="sm:col-span-2"
                  label="Welcome heading"
                  optional
                  error={errors.widgetWelcomeHeading}
                >
                  <Input
                    value={values.widgetWelcomeHeading ?? ""}
                    onChange={(e) => setField("widgetWelcomeHeading", e.target.value)}
                    placeholder="Welcome to AI assistant"
                  />
                </FormField>
                <FormField
                  className="sm:col-span-2"
                  label="Welcome description"
                  optional
                  error={errors.widgetWelcomeDescription}
                >
                  <Textarea
                    value={values.widgetWelcomeDescription ?? ""}
                    onChange={(e) =>
                      setField("widgetWelcomeDescription", e.target.value)
                    }
                    rows={2}
                    placeholder="Start a lead or try one of these:"
                  />
                </FormField>
              </div>

              <div className="grid gap-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Welcome quick questions (up to 3)
                </p>
                {(
                  [
                    ["widgetQuickQuestion1", "Question 1", errors.widgetQuickQuestion1],
                    ["widgetQuickQuestion2", "Question 2", errors.widgetQuickQuestion2],
                    ["widgetQuickQuestion3", "Question 3", errors.widgetQuickQuestion3],
                  ] as const
                ).map(([field, label, error]) => (
                  <FormField key={field} label={label} optional error={error}>
                    <Input
                      value={(values[field] as string | undefined) ?? ""}
                      onChange={(e) => setField(field, e.target.value)}
                    />
                  </FormField>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <ColorField
                  label="Primary background"
                  value={values.widgetPrimaryBg ?? ""}
                  error={errors.widgetPrimaryBg}
                  onChange={(v) => setField("widgetPrimaryBg", v)}
                />
                <ColorField
                  label="Primary text"
                  value={values.widgetPrimaryFg ?? ""}
                  error={errors.widgetPrimaryFg}
                  onChange={(v) => setField("widgetPrimaryFg", v)}
                />
                <ColorField
                  label="Secondary background"
                  value={values.widgetSecondaryBg ?? ""}
                  error={errors.widgetSecondaryBg}
                  onChange={(v) => setField("widgetSecondaryBg", v)}
                />
                <ColorField
                  label="Secondary text"
                  value={values.widgetSecondaryFg ?? ""}
                  error={errors.widgetSecondaryFg}
                  onChange={(v) => setField("widgetSecondaryFg", v)}
                />
                <FormField
                  className="sm:col-span-2"
                  label="Logo URL"
                  optional
                  error={errors.widgetLogoUrl}
                  description="Square image (40×40 in the header, larger as the floating launcher). Any reachable https URL."
                >
                  <Input
                    value={values.widgetLogoUrl ?? ""}
                    onChange={(e) => setField("widgetLogoUrl", e.target.value)}
                    placeholder="https://cdn.dealer.example.com/logo.png"
                  />
                </FormField>
              </div>
            </fieldset>
          )}

          {mode === "edit" && channel != null && isTokenChannelType(channel.type) && (
            <ChannelOnboardingSection
              dealershipId={dealershipId}
              channelId={channel.id}
              channelType={channel.type}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {mode === "edit" ? "Close" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? "Saving…"
              : mode === "create"
                ? "Create channel"
                : "Save channel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirmation dialog for channel deletion. Phone channels are
 * managed by the Telephony tab — deletion is blocked here.
 */
function DeleteChannelDialog({
  channel,
  isPending,
  onConfirm,
  onCancel,
}: {
  channel: Channel | null;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isPhone = channel?.type === "phone";
  const open = Boolean(channel);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isPhone ? "Managed by Telephony" : "Delete channel"}</DialogTitle>
          <DialogDescription>
            {channel
              ? isPhone
                ? `“${channel.name}” (${channel.e164}) is a mirror of a Telephony Account phone number. Release it from the Telephony tab — the mirror channel disappears automatically.`
                : `“${channel.name}” will be removed. Leads already linked to this channel will be deleted along with it.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {isPhone ? "Close" : "Cancel"}
          </Button>
          {!isPhone && (
            <Button variant="destructive" disabled={isPending} onClick={onConfirm}>
              {isPending ? "Deleting…" : "Delete channel"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


async function invalidateChannels(
  qc: ReturnType<typeof useQueryClient>,
  dealershipId?: string,
) {
  if (!dealershipId) return;
  await Promise.all([
    qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "channels"] }),
    qc.invalidateQueries({ queryKey: ["dealership", dealershipId] }),
  ]);
}

/**
 * Surfaces the auto-generated integration token + the snippet the
 * dealer pastes (embed `<script>` for `web`, starter WebSocket JS for
 * `api`). Both are readonly — there's no token rotation UI yet;
 * recreating the channel rotates the token.
 */
function ChannelOnboardingSection({
  dealershipId,
  channelId,
  channelType,
}: {
  dealershipId: string;
  channelId: string;
  channelType: ChannelType;
}) {
  const query = useQuery({
    queryKey: ["admin-bdc", dealershipId, "channel-onboarding", channelId],
    queryFn: () => adminApi.getChannelOnboarding(dealershipId, channelId),
  });
  const onboarding = query.data;
  return (
    <fieldset className="grid gap-3">
      <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
        Integration
      </legend>
      {query.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading integration details…</p>
      ) : query.isError || !onboarding ? (
        <p className="text-xs text-destructive">
          Couldn't load integration details. Try reopening the dialog.
        </p>
      ) : (
        <>
          <CopyableField
            label="Integration token"
            value={onboarding.token ?? ""}
            description="High-entropy bearer that authenticates inbound widget / API traffic to lumika. Treat it like a password."
          />
          {channelType === "web" ? (
            <CopyableBlock
              label="Embed snippet"
              value={onboarding.embedSnippet ?? ""}
              description="Paste before `</body>` on every page that should show the chat widget."
            />
          ) : (
            <CopyableBlock
              label="JavaScript starter"
              value={buildApiStarter(onboarding.token, onboarding.websocketUrl)}
              description="Drop-in template for a Node/browser client. Sends one message and logs every server event."
            />
          )}
        </>
      )}
    </fieldset>
  );
}

function buildApiStarter(token: string | null, wsUrl: string | null): string {
  const safeToken = token ?? "YOUR_TOKEN";
  const safeUrl = wsUrl ?? "wss://api.example.com/api/v1/widget/lead";
  return `// Connect to lumika and send a message.
const ws = new WebSocket("${safeUrl}?token=${safeToken}");
ws.addEventListener("message", (e) => {
  const event = JSON.parse(e.data);
  console.log(event); // agent_start | turn_start | message_end | turn_end | error
});
ws.addEventListener("open", () => {
  ws.send(JSON.stringify({ type: "user_message", text: "Hello" }));
});`;
}

/**
 * Native `<input type="color">` paired with a `#rrggbb` text mirror.
 * The text input lets the admin clear the value (color picker has no
 * "unset" affordance) and paste/copy hex codes directly. The picker
 * just provides a visual swatch + GUI selection. They stay in sync.
 */
function ColorField({
  label,
  value,
  error,
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  // Color input requires a real hex; default to a neutral mid-grey
  // when the form value is empty so the swatch isn't a black box.
  const pickerValue = /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#808080";
  return (
    <FormField label={label} optional error={error}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={pickerValue}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 cursor-pointer rounded-md border border-border bg-transparent p-1"
          aria-label={`${label} color picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#rrggbb"
          className="font-mono"
          aria-label={`${label} hex value`}
        />
      </div>
    </FormField>
  );
}

function CopyableField({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description?: string;
}) {
  return (
    <FormField label={label} description={description}>
      <div className="flex items-center gap-2">
        <Input value={value} readOnly className="font-mono text-xs" />
        <CopyButton value={value} label={label} />
      </div>
    </FormField>
  );
}

function CopyableBlock({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description?: string;
}) {
  return (
    <FormField label={label} description={description}>
      <div className="flex items-start gap-2">
        <pre className="flex-1 overflow-x-auto rounded-md border border-border bg-muted/40 p-3 text-xs font-mono whitespace-pre-wrap break-all">
          {value}
        </pre>
        <CopyButton value={value} label={label} />
      </div>
    </FormField>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          toast.success("Copied");
        } catch {
          toast.error("Copy failed — select the text and copy manually.");
        }
      }}
    >
      <Copy className="h-4 w-4" />
    </Button>
  );
}
